/**
 * @fileoverview Badge definitions and hook for fetching user badge progress
 * 
 * 12 achievement badges with gradient colors, editorial textures, icons,
 * and threshold-based unlocking. Progress calculated client-side from
 * profile stats + earned badges from DB.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useFeatureFlags, FeatureFlagId } from '@/hooks/useFeatureFlags';
import { useProfileStats } from '@/hooks/useProfileStats';
import { useEngagementStats } from '@/hooks/useEngagementStats';
import {
  Bookmark, FolderHeart, ThumbsUp, Compass,
  Library, LayoutGrid, Sparkles, MessageCircleHeart,
  Flame, Palette, Shirt, Mountain, Crown, Leaf,
  DollarSign, TrendingUp, BarChart3, Brain, Eye,
  type LucideIcon
} from 'lucide-react';

export type TextureKey = 'linen' | 'grid' | 'canvas' | 'paper' | 'mosaic' | 'silk' | 'brushstroke' | 'ember' | 'marble' | 'knit' | 'sand' | 'foil' | 'coin' | 'prism';

export interface BadgeDefinition {
  id: string;
  name: string;
  description: string;
  icon: LucideIcon;
  gradientFrom: string;
  gradientTo: string;
  threshold: number;
  statKey: 'saves' | 'collections' | 'votes' | 'polls' | 'tryons' | 'streak' | 'extension' | 'spring_cleans' | 'price_guess_games' | 'price_guess_perfect' | 'price_guess_level' | 'referrals';
  texture: TextureKey;
  category?: string;
  featureFlag?: FeatureFlagId;
}

export const BADGE_DEFINITIONS: BadgeDefinition[] = [
  { id: 'first_save', name: 'First Save', description: 'Save your first item', icon: Bookmark, gradientFrom: '#38bdf8', gradientTo: '#3b82f6', threshold: 1, statKey: 'saves', texture: 'linen' },
  { id: 'first_collection', name: 'First Collection', description: 'Create your first collection', icon: FolderHeart, gradientFrom: '#2dd4bf', gradientTo: '#10b981', threshold: 1, statKey: 'collections', texture: 'grid' },
  { id: 'first_vote', name: 'First Vote', description: 'Vote on your first poll', icon: ThumbsUp, gradientFrom: '#a3e635', gradientTo: '#22c55e', threshold: 1, statKey: 'votes', texture: 'canvas' },
  { id: 'safari_explorer', name: 'Explorer', description: 'Enable the Safari extension', icon: Compass, gradientFrom: '#0ea5e9', gradientTo: '#2563eb', threshold: 1, statKey: 'extension', texture: 'silk' },
  { id: 'curator', name: 'Curator', description: 'Saved 25 items', icon: Library, gradientFrom: '#60a5fa', gradientTo: '#6366f1', threshold: 25, statKey: 'saves', texture: 'paper' },
  { id: 'collector', name: 'Collector', description: 'Create 5 collections', icon: LayoutGrid, gradientFrom: '#22d3ee', gradientTo: '#14b8a6', threshold: 5, statKey: 'collections', texture: 'mosaic' },
  { id: 'trendsetter', name: 'Trendsetter', description: 'Try on your first look', icon: Sparkles, gradientFrom: '#f472b6', gradientTo: '#f43f5e', threshold: 1, statKey: 'tryons', texture: 'silk', featureFlag: 'try_on' },
  { id: 'social_butterfly', name: 'Social Butterfly', description: 'Vote on 25 polls', icon: MessageCircleHeart, gradientFrom: '#fb7185', gradientTo: '#ec4899', threshold: 25, statKey: 'votes', texture: 'brushstroke', featureFlag: 'polls' },
  { id: 'week_warrior', name: 'Week Warrior', description: 'Maintain a 7-day streak', icon: Flame, gradientFrom: '#fb923c', gradientTo: '#ef4444', threshold: 7, statKey: 'streak', texture: 'ember' },
  { id: 'style_maven', name: 'Style Maven', description: 'Save 100 items', icon: Palette, gradientFrom: '#a78bfa', gradientTo: '#8b5cf6', threshold: 100, statKey: 'saves', texture: 'marble' },
  { id: 'tryon_pro', name: 'Try-On Pro', description: 'Complete 25 try-ons', icon: Shirt, gradientFrom: '#e879f9', gradientTo: '#ec4899', threshold: 25, statKey: 'tryons', texture: 'knit', featureFlag: 'try_on' },
  { id: 'month_strong', name: 'Month Strong', description: 'Maintain a 30-day streak', icon: Mountain, gradientFrom: '#fbbf24', gradientTo: '#f97316', threshold: 30, statKey: 'streak', texture: 'sand' },
  { id: 'style_icon', name: 'Style Icon', description: 'Maintain a 180-day streak', icon: Crown, gradientFrom: '#facc15', gradientTo: '#f43f5e', threshold: 180, statKey: 'streak', texture: 'foil' },
  { id: 'spring_clean', name: 'Spring Clean', description: 'Complete a Spring Clean session', icon: Leaf, gradientFrom: '#4ade80', gradientTo: '#16a34a', threshold: 1, statKey: 'spring_cleans', texture: 'canvas' },
  // Price Sense badges
  { id: 'rookie_guesser', name: 'Rookie Guesser', description: 'Play your first Price Guess', icon: DollarSign, gradientFrom: '#94a3b8', gradientTo: '#64748b', threshold: 1, statKey: 'price_guess_games', texture: 'coin', category: 'Price Sense', featureFlag: 'price_guess' },
  { id: 'smart_shopper', name: 'Smart Shopper', description: 'Get 5 perfect guesses', icon: TrendingUp, gradientFrom: '#38bdf8', gradientTo: '#0284c7', threshold: 5, statKey: 'price_guess_perfect', texture: 'prism', category: 'Price Sense', featureFlag: 'price_guess' },
  { id: 'price_analyst', name: 'Price Analyst', description: 'Reach Level 10', icon: BarChart3, gradientFrom: '#a78bfa', gradientTo: '#7c3aed', threshold: 10, statKey: 'price_guess_level', texture: 'marble', category: 'Price Sense', featureFlag: 'price_guess' },
  { id: 'market_guru', name: 'Market Guru', description: 'Get 25 perfect guesses', icon: Brain, gradientFrom: '#f472b6', gradientTo: '#db2777', threshold: 25, statKey: 'price_guess_perfect', texture: 'silk', category: 'Price Sense', featureFlag: 'price_guess' },
  { id: 'oracle_of_prices', name: 'Oracle of Prices', description: 'Reach Level 20', icon: Eye, gradientFrom: '#fbbf24', gradientTo: '#f59e0b', threshold: 20, statKey: 'price_guess_level', texture: 'foil', category: 'Price Sense', featureFlag: 'price_guess' },
  // Referral badges
  { id: 'connector', name: 'Connector', description: 'Invite 5 friends', icon: Sparkles, gradientFrom: '#FF5C4D', gradientTo: '#FF6F3B', threshold: 5, statKey: 'referrals', texture: 'silk', category: 'Referral' },
  { id: 'ambassador', name: 'Ambassador', description: 'Invite 10 friends', icon: Crown, gradientFrom: '#FF5C4D', gradientTo: '#f97316', threshold: 10, statKey: 'referrals', texture: 'foil', category: 'Referral' },
  { id: 'tastemaker', name: 'Tastemaker', description: 'Invite 25 friends', icon: Palette, gradientFrom: '#f43f5e', gradientTo: '#a855f7', threshold: 25, statKey: 'referrals', texture: 'marble', category: 'Referral' },
  { id: 'legend', name: 'Legend', description: 'Invite 50 friends', icon: Mountain, gradientFrom: '#fbbf24', gradientTo: '#f43f5e', threshold: 50, statKey: 'referrals', texture: 'foil', category: 'Referral' },
];

export interface BadgeProgress {
  badge: BadgeDefinition;
  earned: boolean;
  earnedAt?: string;
  currentValue: number;
  progress: number; // 0-100
}

export function useBadgeProgress(userId?: string) {
  const { user } = useAuth();
  const { isEnabled } = useFeatureFlags();
  const targetUserId = userId || user?.id;

  // Filter out badges for disabled features
  const activeBadges = BADGE_DEFINITIONS.filter(b => !b.featureFlag || isEnabled(b.featureFlag));
  const { data: profileStats } = useProfileStats(targetUserId);
  const { data: engagementStats } = useEngagementStats(targetUserId);

  const { data: earnedBadges = [] } = useQuery({
    queryKey: ['user-badges-earned', targetUserId],
    queryFn: async () => {
      if (!targetUserId) return [];
      const { data, error } = await supabase
        .from('user_badges')
        .select('badge_id, earned_at')
        .eq('user_id', targetUserId);
      if (error) {
        console.error('Error fetching badges:', error);
        return [];
      }
      return data;
    },
    enabled: !!targetUserId,
    staleTime: 60 * 1000,
  });

  const { data: extensionEnabled = false } = useQuery({
    queryKey: ['safari-extension-flag', targetUserId],
    queryFn: async () => {
      if (!targetUserId) return false;
      const { data } = await supabase
        .from('profiles')
        .select('safari_extension_enabled')
        .eq('user_id', targetUserId)
        .single();
      return (data as any)?.safari_extension_enabled ?? false;
    },
    enabled: !!targetUserId,
    staleTime: 60 * 1000,
  });

  const { data: springCleanCount = 0 } = useQuery({
    queryKey: ['spring-clean-count', targetUserId],
    queryFn: async () => {
      if (!targetUserId) return 0;
      const { data } = await supabase
        .from('feature_usage')
        .select('usage_count')
        .eq('user_id', targetUserId)
        .eq('feature_type', 'spring_clean');
      return (data || []).reduce((sum, r) => sum + (r.usage_count || 0), 0);
    },
    enabled: !!targetUserId,
    staleTime: 60 * 1000,
  });

  const { data: referralCount = 0 } = useQuery({
    queryKey: ['referral-count', targetUserId],
    queryFn: async () => {
      if (!targetUserId) return 0;
      const { count } = await supabase
        .from('referrals')
        .select('*', { count: 'exact', head: true })
        .eq('referrer_id', targetUserId);
      return count || 0;
    },
    enabled: !!targetUserId,
    staleTime: 60 * 1000,
  });

  // Also trigger a badge check for current user
  useQuery({
    queryKey: ['badge-check-trigger', targetUserId],
    queryFn: async () => {
      if (!targetUserId || targetUserId !== user?.id) return null;
      await supabase.rpc('check_and_award_badges', { p_user_id: targetUserId });
      return true;
    },
    enabled: !!targetUserId && targetUserId === user?.id,
    staleTime: 5 * 60 * 1000,
  });

  const getStatValue = (statKey: BadgeDefinition['statKey']): number => {
    if (!profileStats && !engagementStats) return 0;
    switch (statKey) {
      case 'saves': return profileStats?.itemsSaved ?? 0;
      case 'collections': return profileStats?.collectionsCount ?? 0;
      case 'votes': return 0;
      case 'polls': return profileStats?.pollsCreated ?? 0;
      case 'tryons': return profileStats?.tryOnsCount ?? 0;
      case 'streak': return Math.max(engagementStats?.currentStreak ?? 0, engagementStats?.longestStreak ?? 0);
      case 'extension': return extensionEnabled ? 1 : 0;
      case 'spring_cleans': return springCleanCount;
      case 'price_guess_games': return 0;
      case 'price_guess_perfect': return 0;
      case 'price_guess_level': return 0;
      case 'referrals': return referralCount;
      default: return 0;
    }
  };

  const badgeProgress: BadgeProgress[] = activeBadges.map(badge => {
    const earned = earnedBadges.some(eb => eb.badge_id === badge.id);
    const earnedAt = earnedBadges.find(eb => eb.badge_id === badge.id)?.earned_at;
    const currentValue = getStatValue(badge.statKey);
    const progress = earned ? 100 : Math.min(100, Math.round((currentValue / badge.threshold) * 100));

    return { badge, earned, earnedAt, currentValue, progress };
  });

  const earnedCount = badgeProgress.filter(bp => bp.earned).length;

  return { badgeProgress, earnedCount, totalBadges: activeBadges.length };
}
